/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import Modal from './modal/modal';
import Typography from './typography/typography';
import Button from './button/button';
import ModalLoader from './loaders/modal-loader';
import { DynamicInput, DynamicSearchSelect, DynamicToggleButton, IModalComponentProps } from './form-control';

interface IFormValues {
    department: {
        company_id: string | null;
        department_code: string;
        name: string;
        parent_department_id: string | null;
        department_head_id: string | null;
        cost_code: string;
        status: boolean;
        description: string;
    };
}

interface IDepartmentAddModalProps extends IModalComponentProps {
    initialValues?: {
        parent_department_id?: string | number | null;
        company_id?: string | number | null;
    };
}

const departmentValidationSchema = Yup.object().shape({
    department: Yup.object().shape({
        company_id: Yup.string().nullable().required('Company is required'),
        department_code: Yup.string().required('Department Code is required'),
        name: Yup.string().required('Department Name is required'),
        parent_department_id: Yup.string().nullable(),
        department_head_id: Yup.string().nullable(),
        cost_code: Yup.string(),
        status: Yup.boolean(),
        description: Yup.string(),
    })
});

const getDefaultValues = (initialValues?: IDepartmentAddModalProps['initialValues']) => ({
    department: {
        company_id: initialValues?.company_id ?? null,
        department_code: '',
        name: '',
        parent_department_id: initialValues?.parent_department_id ?? null,
        department_head_id: null,
        cost_code: '',
        status: true,
        description: '',
    }
});

export const DepartmentAddModal: React.FC<IDepartmentAddModalProps> = ({
    isOpen,
    setIsOpen,
    onSave,
    isLoading,
    modalProps,
    initialValues,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, trigger, watch } = useForm<any>({
        mode: 'all',
        resolver: yupResolver(departmentValidationSchema),
        defaultValues: getDefaultValues(initialValues)
    });

    const rootCompanyId = watch('department.company_id');

    useEffect(() => {
        if (isOpen) {
            reset(getDefaultValues(initialValues));
        }
    }, [isOpen]);

    const handleModalClose = () => {
        reset(getDefaultValues(initialValues));
        setIsOpen();
    };

    const onSubmit = async (values: IFormValues) => {
        const isValid = await trigger();
        if (!isValid) return;
        await onSave(values as any);
        handleModalClose();
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleModalClose}
            maxWidth='sm'
            fullWidth
            {...modalProps}
        >
            <>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {isLoading && <ModalLoader />}
                    <DialogTitle className='brandAddModal--DialogTitle'>
                        <Typography type='s3' color='inherit' weight='medium'>
                            {t('hrms.organization_structure.departmentModal.title', 'Add Department')}
                        </Typography>
                        <IconButton onClick={handleModalClose}>
                            <Close fontSize='small' />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className='brandAddModal--DialogContents'>
                        <Grid container rowGap={1.5} columnSpacing={3} pt={3}>
                            {/* Company - Required */}
                            <Grid item sm={12}>
                                <DynamicSearchSelect
                                    label={t('hrms.organization_structure.departmentModal.company_label', 'Company')}
                                    name='company_id'
                                    placeholder={t('hrms.organization_structure.departmentModal.company_placeholder', 'Select Company')}
                                    fieldArrayName='department'
                                    formControl={control as any}
                                    apiType='company'
                                    required
                                    options={[]}
                                />
                            </Grid>

                            {/* Department Code - Required */}
                            <Grid item sm={6}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t('hrms.organization_structure.departmentModal.code_label', 'Department Code')}
                                    name='department_code'
                                    fieldArrayName='department'
                                    placeholder={t('hrms.organization_structure.departmentModal.code_placeholder', 'Enter Department Code')}
                                    required
                                />
                            </Grid>

                            {/* Department Name - Required */}
                            <Grid item sm={6}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t('hrms.organization_structure.departmentModal.name_label', 'Department Name')}
                                    name='name'
                                    fieldArrayName='department'
                                    placeholder={t('hrms.organization_structure.departmentModal.name_placeholder', 'Enter Department Name')}
                                    required
                                />
                            </Grid>

                            {/* Parent Department - Optional */}
                            <Grid item sm={6}>
                                <DynamicSearchSelect
                                    label={t('hrms.organization_structure.departmentModal.parent_department_label', 'Parent Department')}
                                    name='parent_department_id'
                                    placeholder={t('hrms.organization_structure.departmentModal.parent_department_placeholder', 'Select Parent Department')}
                                    fieldArrayName='department'
                                    formControl={control as any}
                                    apiType='department'
                                    required={false}
                                    options={[]}
                                    customeFilter={{
                                        '&is_parent.eq': true,
                                        '&company_id.eq': rootCompanyId || 0
                                    }}
                                />
                            </Grid>

                            {/* Department Head - Optional */}
                            <Grid item sm={6}>
                                <DynamicSearchSelect
                                    label={t('hrms.organization_structure.departmentModal.department_head_label', 'Department Head')}
                                    name='department_head_id'
                                    placeholder={t('hrms.organization_structure.departmentModal.department_head_placeholder', 'Select Department Head')}
                                    fieldArrayName='department'
                                    formControl={control as any}
                                    apiType='employees'
                                    required={false}
                                    options={[]}
                                    customeFilter={{
                                        '&company_id.eq': rootCompanyId || 0,
                                        '&is_active.eq': 1
                                    }}
                                />
                            </Grid>

                            {/* Cost Code - Optional */}
                            <Grid item sm={6}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t('hrms.organization_structure.departmentModal.cost_code_label', 'Cost Code')}
                                    name='cost_code'
                                    fieldArrayName='department'
                                    placeholder={t('hrms.organization_structure.departmentModal.cost_code_placeholder', 'Enter Cost Code')}
                                />
                            </Grid>

                            {/* Status Toggle */}
                            <Grid item sm={6}>
                                <DynamicToggleButton
                                    formControl={control as any}
                                    name='status'
                                    fieldArrayName='department'
                                    label={t('hrms.organization_structure.departmentModal.status_label', 'Status')}
                                    title={t('common.active', 'Active')}
                                    titlePosition='end'
                                    position={3}
                                />
                            </Grid>

                            {/* Description - Optional, full width */}
                            <Grid item sm={12}>
                                <DynamicInput
                                    type='textarea'
                                    formControl={control}
                                    label={t('hrms.organization_structure.departmentModal.description_label', 'Description')}
                                    name='description'
                                    fieldArrayName='department'
                                    placeholder={t('hrms.organization_structure.departmentModal.description_placeholder', 'Enter Description')}
                                    is_multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions className='brandAddModal--DialogAction'>
                        <span></span>
                        <Box display='flex' gap={1.5}>
                            <Button
                                className='brandAddModal--DialogAction--WhiteBtn'
                                onClick={handleModalClose}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button type='button' onClick={handleSubmit(onSubmit)}>
                                {t('common.save')}
                            </Button>
                        </Box>
                    </DialogActions>
                </form>
            </>
        </Modal>
    );
};
